
import { backofficeQueryURL, sec } from "../config";
import { getAsync, postAsync, putAsync } from "./ajax";

export class BackofficeError extends Error {
  readonly status: number
  readonly body: string

  constructor(status: number, body: string) {
    super(`Server returned status: ${status}`)
    this.name = 'BackofficeError'
    this.status = status
    this.body = body
  }
}

class FetchPromise<T> {
  
  private res: (value: T | PromiseLike<T>) => void
  private rej: (reason?: any) => void
  private readonly promise: Promise<T>

  constructor() {
    this.promise = new Promise<T>((resolve, reject) => {
      this.res = resolve
      this.rej = reject
    })
  }

  then<TResult1 = T, TResult2 = never, TResult3 = never>(onfulfilled?: ((value: T) => TResult1 | PromiseLike<TResult1>) | null | undefined, onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null | undefined, onempty?: (() => TResult3 | PromiseLike<TResult3> | null | undefined)): Promise<TResult1 | TResult2> {
    return this.promise.then(onfulfilled, onrejected)
  }

  finally(onfinally?: (() => void) | null | undefined): Promise<T> {
    return this.promise.finally(onfinally)
  }

  catch (onRejected?: (reason: any) => PromiseLike<never>) : Promise<T> {return this.promise.catch(onRejected)}

  resolve(value: T | PromiseLike<T>):void { return this.res(value)}

  reject(reason?:any):void {return this.rej(reason)}
}

export const SaveToBackoffice = (path: string, payload: any) : FetchPromise<void> => {
  const url = `${backofficeQueryURL()}/${path}`;
  console.debug('SetToBackoffice', url)

  const promise = new FetchPromise<void>()
    
  sec.getAccessTokenSilently()
    .then(token => putAsync(url, JSON.stringify(payload), token))
    .then(r => {
      if (!r.ok) {
        throw new Error(`Server returned status: ${r.status}`);
      }
      if(r.status === 204)
      {
        return undefined
      }
      return 
    })
    .then(data => {
      promise.resolve(data as void)
      return promise
    })
  .catch(error =>{
    console.error(`Error fetching ${url}.`, error)
    promise.reject(error)
    return promise
  });

  return promise;
}


export const FetchFromBackoffice = <T>(path: string) : FetchPromise<T> => {
  const url = `${backofficeQueryURL()}/${path}`;
  console.debug('FetchFromBackoffice', url)

  const promise = new FetchPromise<T>()
    
  sec.getAccessTokenSilently()
    .then(token => getAsync(url, token))
    .then(async r => {
      if (!r.ok) {
        const body = await r.text().catch(() => '');
        throw new BackofficeError(r.status, body);
      }
      if(r.status === 204)
      {
        return undefined
      }
      return r.json();
    })
    .then(data => {
      promise.resolve(data as T)
      return promise
    })
  .catch(error =>{
    console.error(`Error fetching ${url}.`, error)
    promise.reject(error)
    return promise
  });

  return promise;
}

export const FetchImageFromBackoffice = <T>(path: string) : FetchPromise<T> => {
  const url = `${backofficeQueryURL()}/${path}`;
  console.debug('FetchFromBackoffice', url)

  const promise = new FetchPromise<T>()
    
  sec.getAccessTokenSilently()
    .then(token => getAsync(url, token))
    .then(r => {
      if(r.status === 204)
      {
        return undefined
      }
      return r.blob();
    })
    .then(data => {
      promise.resolve(data as T)
    })
  .catch(error =>{
    console.error(`Error fetching ${url}.`, error)
    promise.reject(error)
  });

  return promise;
}

export const CreateToBackoffice = <T>(path: string, payload: any): FetchPromise<T> => {
  const url = `${backofficeQueryURL()}/${path}`;
  const promise = new FetchPromise<T>();

  sec.getAccessTokenSilently()
    .then(token => postAsync(url, JSON.stringify(payload), token))
    .then(async r => {
      if (!r.ok) {
        throw new Error(`Server returned status: ${r.status}`);
      }

      const location = r.headers.get('Location');
      if (location) {
        const savedVariantUrl = new URL(location, `${backofficeQueryURL()}/`).toString();
        const savedVariantResponse = await getAsync(savedVariantUrl, await sec.getAccessTokenSilently());

        if (!savedVariantResponse.ok) {
          throw new Error(`Server returned status: ${savedVariantResponse.status}`);
        }

        return savedVariantResponse.json();
      }

      if (r.status === 204) {
        return undefined;
      }

      return r.json();
    })
    .then(data => promise.resolve(data as T))
    .catch(error => {
      console.error(`Error creating resource at ${url}`, error);
      promise.reject(error);
    });

  return promise;
};